import { lazyPanel } from '../shared/lazyPanel';
import { studioRequestId } from '../commands/requestId';
/**
 * UPT-2096 (P1-a, §4.1) — dialogue « Importer un prototype » : quatre étapes
 * (Fichiers, Prompt, Déclarations, Vérification), zone de dépôt + alternative
 * clavier, préparation des images (ré-encodage navigateur), inventaire
 * indicatif et avertissements d'aperçu à l'étape 4, envoi (création, puis
 * sources HTML de rang 1..n, puis images, une par requête) avec reprise sur
 * échec. Le prompt ne vit que dans l'état du dialogue (jamais dans un stockage
 * du navigateur). L2 : plusieurs HTML, documents Markdown (rôle `prompt` /
 * `notes`), liste des pièces, prompt pré-rempli, sélecteur de source à l'étape 4.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from '@/react-intl';
// react-dropzone est publié en CommonJS : l'import nommé casse le rendu serveur de Vite (« Named export 'useDropzone'
// not found »). Import de l'espace de noms puis lecture tolérante (ESM ou interop CJS), comme SpectrumFileInput.
import * as ReactDropzone from 'react-dropzone';
const useDropzone: typeof import('react-dropzone').useDropzone =
  ReactDropzone.useDropzone ??
  ((ReactDropzone as unknown as { default?: typeof ReactDropzone }).default ?? ReactDropzone).useDropzone;
import { Modal } from '@/design-system/ds-0/components/Modal';
import useIsMobile, { TOUCH_DENSITY_BREAKPOINT } from '@/uptk/core/hooks/useIsMobile';
import { fetchModuleCatalog } from '@/uptk/core/components/marketplace/api';
import { resolveSaasContext } from '@/uptk/core/api/saasContext';
import type { ModuleCatalog } from '@/uptk/core/components/marketplace/types';
import {
  PROTOTYPE_LIMITS,
  type PrototypeDerivationIntent,
  type PrototypeDocumentRole,
  type PrototypeImport,
  type PrototypeToolName,
} from '../data/contracts';
import {
  checkSelection,
  reorderSources,
  utf8ByteLength,
  type FileLike,
  type PreparedHtml,
  type PreparedImage,
  type PreparedMarkdown,
} from './files';
import { prepareHtmlFile, prepareMarkdownFile, setDocumentRole, suggestImportTitle } from './prepareFiles';
import ImportPieceList, { formatPieceSize } from './ImportPieceList';
import SourceSelector from './SourceSelector';
import { reencodeImage } from './reencodeImage';
import { createImageUrl, revokeImageUrl } from './imageFiles';
import { normalizePromptText, importBlockers, buildPrototypeImportInput, importMetadataReviewed, type ImportCommandParameters, type ImportDraft } from './importInput';
import { computePreviewWarnings } from './warnings';
import { extractPrototypeScreen, buildPrototypeInventory, inventorySourceOf } from './inventory';
import { readPrototypeError, type PrototypeUiErrorCode } from './prototypeErrors';
import {
  prototypeErrorLabel,
  importBlockerLabel,
  rejectionReasonLabel,
  prototypeToolLabel,
  prototypeDocumentRoleLabel,
} from './prototypeI18n';
import PrototypeWarningsView from './PrototypeWarningsView';
import PrototypeInventoryView from './PrototypeInventoryView';
import { externalResourceHosts } from './warnings';
import { useModuleStudio } from '../data/ModuleStudioDataProvider';
import StatusChip from '../shared/StatusChip';
import StudioCheckbox from '../shared/StudioCheckbox';
import PipelineText from '../pipeline/PipelineText';

const PrototypeSandbox = lazyPanel(() => import('./PrototypeSandbox'));

/** Outil qui a fabriqué la maquette (traçabilité seulement) ; `other` (« Autre / je ne sais pas ») est la valeur par défaut. */
const TOOLS: PrototypeToolName[] = ['claude', 'chatgpt', 'gemini', 'v0', 'lovable', 'bolt', 'figma-make', 'handmade', 'other'];
const DEFAULT_TOOL: PrototypeToolName = 'other';

export interface ImportPrototypeDialogProps {
  open: boolean;
  dossierRef: string;
  /** « Importer une nouvelle version » : import à pré-remplacer. */
  supersedes?: PrototypeImport | null;
  parameters?: Partial<ImportCommandParameters>;
  onClose: () => void;
  onImported: (imp: PrototypeImport) => void;
}

interface Rejection {
  name: string;
  reason: string;
}

// 23.10.2026 : un même fichier refusé pour le même motif n'est listé qu'une fois (le dépôt et le
// sélecteur de fichiers peuvent chacun remonter le refus).
const addRejections = (current: Rejection[], next: Rejection[]): Rejection[] => {
  const merged = [...current];
  for (const item of next) {
    if (!merged.some((existing) => existing.name === item.name && existing.reason === item.reason)) merged.push(item);
  }
  return merged;
};

// i18n Rule 1 : glyphes techniques (séparateurs, flèches, tirets), pas du texte utilisateur — hissés au niveau du module.
const DASH = ' — ';
const DOT = ' · ';
const DERIVATION_INTENTS = ['none', 'on-request', 'standard'] as const;

export default function ImportPrototypeDialog({
  open,
  dossierRef,
  supersedes,
  parameters,
  onClose,
  onImported,
}: ImportPrototypeDialogProps) {
  const intl = useIntl();
  const t = useCallback(
    (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values),
    [intl]
  );
  const isMobile = useIsMobile(TOUCH_DENSITY_BREAKPOINT);
  const { repository } = useModuleStudio();

  const [step, setStep] = useState(1);
  const [htmls, setHtmls] = useState<PreparedHtml[]>([]);
  const [markdowns, setMarkdowns] = useState<PreparedMarkdown[]>([]);
  const [roleAnnouncement, setRoleAnnouncement] = useState('');
  const [previewSource, setPreviewSource] = useState(0);
  const [images, setImages] = useState<PreparedImage[]>([]);
  const [rejections, setRejections] = useState<Rejection[]>([]);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [toolName, setToolName] = useState<PrototypeToolName>(DEFAULT_TOOL);
  const toolId = useId();
  const toolHintId = useId();
  const [toolDetail, setToolDetail] = useState('');
  const [promptText, setPromptText] = useState('');
  // L2 : la zone n'est pré-remplie qu'une fois, tant que l'utilisateur ne l'a pas modifiée à la main.
  const [promptTouched, setPromptTouched] = useState(false);
  const [prefill, setPrefill] = useState<{ text: string; fileName: string } | null>(null);
  // Nom du fichier passé de « prompt » à « notes de conception » depuis l'étape 2 (confirmation sous la zone).
  const [notesFromPrompt, setNotesFromPrompt] = useState<string | null>(null);
  const promptAreaRef = useRef<HTMLTextAreaElement>(null);
  const [derivedFrom, setDerivedFrom] = useState<string[]>([]);
  const [derivation, setDerivation] = useState<PrototypeDerivationIntent>('none');
  const [attestationNoRealData, setAttestationNoRealData] = useState(false);
  const [attestationRights, setAttestationRights] = useState(false);
  const [catalog, setCatalog] = useState<ModuleCatalog[] | null>(null);
  const [catalogError, setCatalogError] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<PrototypeUiErrorCode | null>(null);
  const [failedImages, setFailedImages] = useState<PreparedImage[]>([]);
  const [failedSources, setFailedSources] = useState<PreparedHtml[]>([]);
  const [sending, setSending] = useState(false);
  const thumbnailsRef = useRef<Map<string, string>>(new Map());
  // Reprise sur échec : la création n'est jamais refaite ; les sources partent dans l'ordre (le rang est attribué par le serveur).
  const createdRef = useRef<PrototypeImport | null>(null);
  const commandRequest = useRef<string | null>(null);
  const nextSourceRef = useRef(1);
  const pendingImagesRef = useRef<PreparedImage[]>([]);

  // Pré-remplissage « Importer une nouvelle version » (fichiers et attestations à refaire).
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setHtmls([]);
    setMarkdowns([]);
    setRoleAnnouncement('');
    setPreviewSource(0);
    setPromptTouched(parameters?.promptText !== undefined);
    setPrefill(null);
    setNotesFromPrompt(null);
    createdRef.current = null;
    commandRequest.current = null;
    pendingImagesRef.current = [];
    nextSourceRef.current = 1;
    setFailedSources([]);
    setImages([]);
    setRejections([]);
    setTitle(parameters?.title ?? supersedes?.title ?? '');
    setTitleTouched(parameters?.title !== undefined);
    setToolName(parameters?.toolName ?? supersedes?.toolName ?? DEFAULT_TOOL);
    setToolDetail(parameters?.toolDetail ?? supersedes?.toolDetail ?? '');
    setDerivedFrom(parameters?.derivedFrom ?? supersedes?.derivations.map((d) => d.moduleCode) ?? []);
    setDerivation(parameters?.derivation ?? supersedes?.rights.derivation ?? 'none');
    setAttestationNoRealData(parameters?.attestationNoRealData ?? false);
    setAttestationRights(parameters?.attestationRights ?? false);
    setError(null);
    setFailedImages([]);
    setProgress('');
    if (parameters?.promptText !== undefined) {
      setPromptText(parameters.promptText);
    } else if (supersedes?.hasPrompt) {
      repository.getPrototypePrompt(supersedes.id)
        .then((prompt) => setPromptText(prompt?.promptText ?? ''))
        .catch(() => setPromptText(''));
    } else {
      setPromptText('');
    }
  }, [open, supersedes, repository, parameters]);

  // Miniatures : URL d'objet du PNG ré-encodé, révoquées à la fermeture.
  useEffect(() => {
    const thumbnails = thumbnailsRef.current;
    for (const image of images) {
      if (!thumbnails.has(image.fileName)) {
        const url = createImageUrl(image.png);
        if (url) thumbnails.set(image.fileName, url);
      }
    }
  }, [images]);
  useEffect(() => {
    const thumbnails = thumbnailsRef.current;
    if (!open) {
      thumbnails.forEach((url) => revokeImageUrl(url));
      thumbnails.clear();
    }
  }, [open]);

  // Catalogue chargé à l'ouverture de l'étape 3.
  useEffect(() => {
    if (!open || step !== 3 || catalog !== null || catalogError) return;
    let mounted = true;
    fetchModuleCatalog({ tenantId: resolveSaasContext().tenantId })
      .then((modules) => {
        if (mounted) setCatalog(modules);
      })
      .catch(() => {
        if (mounted) setCatalogError(true);
      });
    return () => {
      mounted = false;
    };
  }, [open, step, catalog, catalogError]);

  const acceptFiles = useCallback(
    async (files: File[]) => {
      setError(null);
      const verdict = checkSelection(files as FileLike[], {
        html: htmls.length,
        markdown: markdowns.length,
        images: images.length,
      });
      setRejections((current) =>
        addRejections(
          current,
          verdict.rejected.map((rejected) => ({ name: files[rejected.index]?.name ?? '', reason: rejected.reason }))
        )
      );
      const batchRoles = markdowns.map((markdown) => markdown.role);
      for (const accepted of verdict.accepted) {
        const file = files[accepted.index];
        if (accepted.kind === 'html') {
          // eslint-disable-next-line no-await-in-loop -- l'ordre de sélection donne le rang des sources
          const result = await prepareHtmlFile(file);
          if ('html' in result) {
            setHtmls((current) => [...current, result.html]);
          } else {
            setRejections((current) => addRejections(current, [{ name: file.name, reason: result.reason }]));
          }
        } else if (accepted.kind === 'markdown') {
          // eslint-disable-next-line no-await-in-loop -- les rôles proposés dépendent des fichiers déjà lus
          const result = await prepareMarkdownFile(file, batchRoles);
          if ('markdown' in result) {
            batchRoles.push(...result.markdowns.map((part) => part.role));
            setMarkdowns((current) => [...current, ...result.markdowns]);
          } else {
            setRejections((current) => addRejections(current, [{ name: file.name, reason: result.reason }]));
          }
        } else {
          setProgress(t('moduleStudio.prototype.dialog.preparingImage'));
          const result = await reencodeImage(file);
          if (result.ok) {
            setImages((current) => {
              const next = [...current, result.ok ? result.image : null].filter(
                (item): item is PreparedImage => item !== null
              );
              return next;
            });
          } else {
            setRejections((current) => addRejections(current, [{ name: file.name, reason: 'size' }]));
          }
          setProgress('');
        }
      }
    },
    [htmls.length, markdowns, images.length, t]
  );

  // Titre proposé (source 0, sinon premier .md, sinon première image) tant que le Maker ne l'a pas modifié.
  useEffect(() => {
    if (titleTouched) return;
    const suggested = suggestImportTitle({
      htmls,
      markdowns,
      imageNames: images.map((image) => image.fileName),
    });
    if (suggested) setTitle(suggested);
  }, [htmls, markdowns, images, titleTouched]);

  // Étape 2 : pré-remplie par le .md de rôle `prompt` si la zone est vide et jamais touchée à la main.
  const promptMarkdown = useMemo(
    () => markdowns.find((markdown) => markdown.role === 'prompt') ?? null,
    [markdowns]
  );
  useEffect(() => {
    if (step !== 2 || promptTouched || promptText !== '' || !promptMarkdown) return;
    const text = normalizePromptText(promptMarkdown.text);
    setPrefill({ text, fileName: promptMarkdown.fileName });
    setPromptText(text);
  }, [step, promptTouched, promptText, promptMarkdown]);

  // Prompt normalisé (comme le serveur) et longueur du fichier de rôle `prompt` : le serveur lit le fichier quand la zone est vide.
  const normalizedPrompt = useMemo(() => normalizePromptText(promptText), [promptText]);
  const promptFileLength = useMemo(
    () => (promptMarkdown ? normalizePromptText(promptMarkdown.text).length : 0),
    [promptMarkdown]
  );
  const promptFileTooLong = promptMarkdown !== null && promptFileLength > PROTOTYPE_LIMITS.promptMaxChars;

  /** Nouvelle liste de documents ; la zone pré-remplie à l'identique est vidée quand son fichier n'est plus le prompt. */
  const applyMarkdowns = (next: PreparedMarkdown[]) => {
    setMarkdowns(next);
    const stillPrompt = next.find((markdown) => markdown.role === 'prompt');
    if (prefill && (!stillPrompt || stillPrompt.fileName !== prefill.fileName)) {
      if (promptText === prefill.text) setPromptText('');
      setPrefill(null);
      setPromptTouched(false);
    }
  };

  const changeRole = (index: number, role: PrototypeDocumentRole) => {
    const result = setDocumentRole(markdowns, index, role);
    applyMarkdowns(result.markdowns);
    setRoleAnnouncement(
      result.switched ? t('moduleStudio.prototype.dialog.pieces.promptSwitched', { name: result.switched }) : ''
    );
  };

  /** Étape 2 : le fichier de rôle `prompt` trop long passe en notes de conception, sur place, et lève le blocage. */
  const switchPromptFileToNotes = () => {
    if (!promptMarkdown) return;
    const index = markdowns.indexOf(promptMarkdown);
    if (index < 0) return;
    changeRole(index, 'notes');
    // Relecture du 18.10 (W10) : la zone n'est vidée que si son texte est encore celui du fichier (pré-remplissage intact) ; un texte retouché par le Maker
    // reste à lui — s'il est toujours trop long, le blocage du texte collé le dit, le fichier part comme notes.
    if (normalizedPrompt === normalizePromptText(promptMarkdown.text)) setPromptText('');
    setNotesFromPrompt(promptMarkdown.fileName);
    promptAreaRef.current?.focus();
  };

  const { getRootProps, getInputProps, open: openPicker } = useDropzone({
    onDrop: (accepted) => void acceptFiles(accepted),
    noClick: true,
    multiple: true,
  });

  const draft: ImportDraft = useMemo(
    () => ({
      title,
      toolName,
      toolDetail,
      promptText,
      htmls,
      markdowns,
      images,
      derivedFrom,
      derivation,
      attestationNoRealData,
      attestationRights,
      supersedesImportId: supersedes?.id ?? null,
    }),
    [title, toolName, toolDetail, promptText, htmls, markdowns, images, derivedFrom, derivation,
      attestationNoRealData, attestationRights, supersedes]
  );
  const blockers = useMemo(() => importBlockers(draft), [draft]);
  useEffect(() => { commandRequest.current = null; }, [draft]);
  const promptTooLong = blockers.includes('prompt-too-long');
  // « Sans prompt, les règles métier seront déduites du seul prototype » : seulement quand il n'y a ni prompt ni notes.
  const hasNotes = markdowns.some((markdown) => markdown.role === 'notes');
  const noPromptNoNotes = normalizedPrompt.trim() === '' && promptMarkdown === null && !hasNotes;

  const preview = useMemo(() => {
    if (step !== 4) return null;
    const docs = htmls.map((source) => new DOMParser().parseFromString(source.text, 'text/html'));
    const warnings = computePreviewWarnings({
      html: docs.map((doc, rank) => ({ doc, text: htmls[rank].text, fallback: htmls[rank].fallback })),
      markdowns: markdowns.map(({ fileName, text, role }) => ({ fileName, text, role })),
      prompt: promptText.trim() ? promptText : null,
      images: images.map((image) => ({
        resized: image.width !== image.sourceWidth || image.height !== image.sourceHeight
          ? `${image.sourceWidth}x${image.sourceHeight} → ${image.width}x${image.height}`
          : null,
      })),
    });
    // Inventaire indicatif : un écran par source (`apercu-{n}`), jamais envoyé.
    const inventory =
      docs.length > 0
        ? {
            ...buildPrototypeInventory(
              docs,
              docs.map(
                (doc, rank) =>
                  extractPrototypeScreen(doc, { captureHeader:true, sourcePieceRef: `apercu-${rank}`, label: htmls[rank].fileName }).screen
              )
            ),
            sources: docs.map((doc, rank) => inventorySourceOf(doc, rank, `apercu-${rank}`)),
          }
        : null;
    return { docs, warnings, inventory };
  }, [step, htmls, markdowns, images, promptText]);
  const sourcePosition = Math.min(previewSource, Math.max(0, htmls.length - 1));

  const send = async () => {
    setSending(true);
    setError(null);
    setFailedImages([]);
    setFailedSources([]);
    try {
      let created = createdRef.current;
      if (created === null) {
        // Séquence L2 : création (metadata, .md, HTML de rang 0, première image), puis sources, puis images.
        created = await repository.createPrototypeImport(dossierRef, buildPrototypeImportInput(draft), {
          commandRequestId: commandRequest.current ?? (commandRequest.current = studioRequestId()),
          html: htmls[0] ?? null,
          htmls,
          markdowns,
          image: images[0] ?? null,
        });
        createdRef.current = created;
        nextSourceRef.current = 1;
        pendingImagesRef.current = images.slice(1);
      }
      let firstFailure: PrototypeUiErrorCode | null = null;
      let unsentSources: PreparedHtml[] = [];
      for (let rank = nextSourceRef.current; rank < htmls.length; rank += 1) {
        const source = htmls[rank];
        setProgress(t('moduleStudio.prototype.dialog.sendingSource', { n: rank + 1, total: htmls.length }));
        try {
          // eslint-disable-next-line no-await-in-loop -- les sources partent une par requête, dans l'ordre des rangs
          await repository.addPrototypeSource(
            created.id,
            source,
            source.fallback
              ? { clientWarnings: [{ code: 'html.encoding_fallback', count: 1, example: null, part: 'html' }] }
              : null
          );
          nextSourceRef.current = rank + 1;
        } catch (sourceError) {
          // eslint-disable-next-line no-await-in-loop -- lecture du code d'erreur de la requête en échec
          const code = (await readPrototypeError(sourceError)).code;
          if (code === 'prototype.sources_complete') {
            nextSourceRef.current = rank + 1; // source déjà reçue : passer à la suivante
            continue;
          }
          // Le rang est attribué par le serveur dans l'ordre d'arrivée : on s'arrête à la première source en échec.
          firstFailure = code;
          unsentSources = htmls.slice(rank);
          break;
        }
      }
      const remaining = pendingImagesRef.current;
      const failed: PreparedImage[] = [];
      for (let index = 0; index < remaining.length; index += 1) {
        const image = remaining[index];
        setProgress(
          `${t('moduleStudio.prototype.dialog.sendingImage')} ${images.indexOf(image) + 1} / ${images.length}`
        );
        try {
          // eslint-disable-next-line no-await-in-loop -- les images partent une par requête, dans l'ordre
          await repository.addPrototypeImage(
            created.id,
            image,
            image.width !== image.sourceWidth || image.height !== image.sourceHeight
              ? {
                  clientWarnings: [{
                    code: 'image.resized',
                    count: 1,
                    example: `${image.sourceWidth}x${image.sourceHeight} → ${image.width}x${image.height}`,
                    part: 'image',
                  }],
                }
              : null
          );
        } catch (imageError) {
          const code = (await readPrototypeError(imageError)).code;
          if (code === 'prototype.images_complete') {
            continue; // image déjà reçue : passer à la suivante
          }
          firstFailure = firstFailure ?? code;
          failed.push(image);
        }
      }
      pendingImagesRef.current = failed;
      setProgress('');
      if (failed.length > 0 || unsentSources.length > 0) {
        setFailedImages(failed);
        setFailedSources(unsentSources);
        setError(firstFailure ?? 'unknown');
        return; // le dialogue reste ouvert : l'import existe (incomplet)
      }
      onImported(await repository.getPrototypeImport(created.id));
    } catch (sendError) {
      setError((await readPrototypeError(sendError)).code);
    } finally {
      setSending(false);
    }
  };

  const retryFailedParts = async () => {
    if (failedImages.length === 0 && failedSources.length === 0) return;
    // Reprend la séquence là où elle s'est arrêtée : la création n'est pas refaite (`createdRef`).
    await send();
  };

  const confirmClose = () => {
    if ((htmls.length > 0 || markdowns.length > 0 || images.length > 0) && !sending) {
      if (!window.confirm(t('moduleStudio.prototype.dialog.closeConfirm'))) return;
    }
    onClose();
  };

  const stepTitles = [
    t('moduleStudio.prototype.dialog.step.files'),
    t('moduleStudio.prototype.dialog.step.prompt'),
    t('moduleStudio.prototype.dialog.step.declarations'),
    t('moduleStudio.prototype.dialog.step.review'),
  ];

  return (
    <Modal
      open={open}
      onClose={confirmClose}
      title={t('moduleStudio.prototype.dialog.title')}
      variant={isMobile ? 'fullscreen' : 'default'}
      size="xl"
      closeLabel={t('moduleStudio.prototype.dialog.close')}
    >
      <div className="flex flex-col gap-4">
        <nav aria-label={t('moduleStudio.prototype.dialog.title')}>
          <ol className="flex flex-wrap gap-2 text-sm">
            {stepTitles.map((label, index) => (
              <li key={label}>
                <StatusChip
                  label={`${index + 1}. ${label}`}
                  tone={index + 1 === step ? 'info' : index + 1 < step ? 'success' : 'neutral'}
                />
                {index + 1 === step && <span className="sr-only" aria-current="step" />}
              </li>
            ))}
          </ol>
          <p className="mt-1 text-sm text-portal-ink-muted">
            {t('moduleStudio.prototype.dialog.stepOf')} {step} / 4 : {stepTitles[step - 1]}
          </p>
        </nav>

        <div aria-live="polite" className="text-sm text-portal-ink-muted">{progress}</div>
        {error && (
          <p role="alert" className="rounded-md border border-danger-solid bg-danger-subtle px-3 py-2 text-sm text-danger-text">
            {prototypeErrorLabel(t, error)}
          </p>
        )}

        {step === 1 && (
          <div className="flex flex-col gap-3">
            <div
              {...getRootProps()}
              className="rounded-md border-2 border-dashed border-portal-border bg-portal-surface-muted p-6 text-center"
            >
              <input {...getInputProps()} aria-label={t('moduleStudio.prototype.dialog.chooseFiles')} />
              <p className="text-sm text-portal-ink">{t('moduleStudio.prototype.dialog.dropzone')}</p>
              <p className="mt-1 text-xs text-portal-ink-muted">{t('moduleStudio.prototype.dialog.acceptedMulti')}</p>
              <button
                type="button"
                className="mt-3 rounded-md border border-portal-border bg-portal-surface px-3 py-1.5 text-sm text-portal-ink"
                onClick={openPicker}
              >
                {t('moduleStudio.prototype.dialog.chooseFiles')}
              </button>
            </div>
            <ImportPieceList
              htmls={htmls.map((source) => ({ fileName: source.fileName, sizeBytes: utf8ByteLength(source.text) }))}
              markdowns={markdowns.map((markdown) => ({
                fileName: markdown.fileName,
                sizeBytes: markdown.bytes.length,
                role: markdown.role,
              }))}
              images={images.map((image) => ({
                fileName: image.fileName,
                sizeBytes: image.png.size,
                thumbnailUrl: thumbnailsRef.current.get(image.fileName),
              }))}
              onMoveHtml={(from, to) => setHtmls((current) => reorderSources(current, from, to))}
              onRemoveHtml={(index) => setHtmls((current) => current.filter((_, position) => position !== index))}
              onRoleChange={changeRole}
              onRemoveMarkdown={(index) => applyMarkdowns(markdowns.filter((_, position) => position !== index))}
              onRemoveImage={(index) => setImages((current) => current.filter((_, position) => position !== index))}
              announcement={roleAnnouncement}
            />
            {htmls.some((source) => source.fallback) && (
              <p className="text-xs text-portal-ink-muted">
                {t('moduleStudio.prototype.dialog.encodingFallback')}
              </p>
            )}
            {rejections.length > 0 && (
              <ul className="flex flex-col gap-1 text-sm text-portal-ink" aria-live="polite">
                {rejections.map((rejection) => (
                  <li key={`${rejection.name}-${rejection.reason}`} className="flex items-start gap-2">
                    <span className="min-w-0 flex-1">
                      {rejection.name}{DASH}{rejectionReasonLabel(t, rejection.reason)}
                    </span>
                    <button
                      type="button"
                      className="ui-focus-ring shrink-0 rounded-md border border-portal-border px-2 py-1 text-xs text-portal-ink pointer-coarse:min-h-11"
                      aria-label={t('moduleStudio.prototype.dialog.rejectionDismissAria', { name: rejection.name })}
                      onClick={() =>
                        setRejections((current) =>
                          current.filter((item) => item.name !== rejection.name || item.reason !== rejection.reason)
                        )
                      }
                    >
                      {t('moduleStudio.prototype.dialog.rejectionDismiss')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.dialog.toolHelpMulti')}</p>
            {images.length > 0 && (
              <p className="rounded-md border border-warning-solid bg-warning-subtle px-3 py-2 text-sm text-warning-text">
                {t('moduleStudio.prototype.dialog.maskRealData')}
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-3">
            <details className="text-sm text-portal-ink-muted"><summary>{t('moduleStudio.data.manifestHelpTitle')}</summary><p>{t('moduleStudio.data.manifestHelp')}</p></details>
            <label className="flex flex-col gap-1 text-sm text-portal-ink">
              {t('moduleStudio.prototype.dialog.promptLabel')}
              <textarea
                ref={promptAreaRef}
                value={promptText}
                onChange={(event) => {
                  setPromptText(event.target.value);
                  setPromptTouched(true);
                }}
                rows={10}
                className="rounded-md border border-portal-border bg-portal-surface p-2 text-portal-ink"
              />
            </label>
            {prefill && promptMarkdown && (
              <p aria-live="polite" className="text-xs text-portal-ink-muted">
                {t('moduleStudio.prototype.dialog.promptFromFile', { fileName: prefill.fileName })}
              </p>
            )}
            {promptTooLong && promptMarkdown && promptFileTooLong && (
              <div className="flex flex-col items-start gap-2 rounded-md border border-warning-solid bg-warning-subtle px-3 py-2 text-sm text-warning-text">
                <p role="alert">
                  <PipelineText
                    id="moduleStudio.prototype.dialog.promptTooLongHint"
                    numbers={{ count: promptFileLength, max: PROTOTYPE_LIMITS.promptMaxChars }}
                  />
                </p>
                <button
                  type="button"
                  className="ui-focus-ring min-h-11 rounded-md border border-portal-border bg-portal-surface px-3 py-1.5 text-sm text-portal-ink"
                  onClick={switchPromptFileToNotes}
                >
                  {t('moduleStudio.prototype.dialog.promptUseAsNotes')}
                </button>
              </div>
            )}
            {promptTooLong && !(promptMarkdown && promptFileTooLong) && (
              <p role="alert" className="text-sm text-danger-text">
                {t('moduleStudio.prototype.blocker.promptTooLong')}
              </p>
            )}
            <p aria-live="polite" className="text-sm text-portal-ink-muted empty:hidden">
              {notesFromPrompt !== null &&
                markdowns.some((markdown) => markdown.fileName === notesFromPrompt && markdown.role === 'notes') && (
                  <PipelineText id="moduleStudio.prototype.dialog.promptNowNotes" codes={{ fileName: notesFromPrompt }} />
                )}
            </p>
            <p className="text-xs text-portal-ink-muted">
              {normalizedPrompt.length} / {PROTOTYPE_LIMITS.promptMaxChars}
              {noPromptNoNotes && (
                <>
                  {DASH}
                  {t('moduleStudio.prototype.dialog.promptRecommended')}
                </>
              )}
            </p>
            <p className="rounded-md border border-info-solid bg-info-subtle px-3 py-2 text-sm text-info-text">
              {t('moduleStudio.prototype.prompt.confidential')}
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm text-portal-ink">
              {t('moduleStudio.prototype.dialog.titleLabel')}
              <input
                type="text"
                value={title}
                maxLength={PROTOTYPE_LIMITS.titleMaxChars}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setTitleTouched(true);
                }}
                className="rounded-md border border-portal-border bg-portal-surface p-2 text-portal-ink"
              />
            </label>
            <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.dialog.makerLine')}</p>
            <div className="flex flex-col gap-1 text-sm text-portal-ink">
              <label htmlFor={toolId}>{t('moduleStudio.prototype.dialog.toolLabel')}</label>
              <select
                id={toolId}
                value={toolName}
                aria-describedby={toolHintId}
                onChange={(event) => setToolName((event.target.value || DEFAULT_TOOL) as PrototypeToolName)}
                className="rounded-md border border-portal-border bg-portal-surface p-2 text-portal-ink"
              >
                {TOOLS.map((tool) => (
                  <option key={tool} value={tool}>
                    {tool === DEFAULT_TOOL ? t('moduleStudio.prototype.dialog.toolOptionOther') : prototypeToolLabel(t, tool)}
                  </option>
                ))}
              </select>
              <p id={toolHintId} className="m-0 text-xs text-portal-ink-muted">{t('moduleStudio.prototype.dialog.toolHint')}</p>
            </div>
            <label className="flex flex-col gap-1 text-sm text-portal-ink">
              {t('moduleStudio.prototype.dialog.toolDetailLabel')}
              <input
                type="text"
                value={toolDetail}
                maxLength={PROTOTYPE_LIMITS.toolDetailMaxChars}
                onChange={(event) => setToolDetail(event.target.value)}
                className="rounded-md border border-portal-border bg-portal-surface p-2 text-portal-ink"
              />
            </label>

            <StudioCheckbox
              checked={derivedFrom.length > 0}
              onChange={(event) => {
                if (!event.target.checked) setDerivedFrom([]);
              }}
              disabled={catalogError}
            >
              {t('moduleStudio.prototype.dialog.derives')}
            </StudioCheckbox>
            {catalogError && (
              <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.dialog.catalogUnavailable')}</p>
            )}
            {catalog && derivedFrom.length >= 0 && (
              <fieldset className="rounded-md border border-portal-border p-2">
                <legend className="text-xs text-portal-ink-muted">
                  {t('moduleStudio.prototype.dialog.derivedModules')}
                </legend>
                <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
                  {catalog.map((module) => (
                    <StudioCheckbox
                      key={module.code}
                      checked={derivedFrom.includes(module.code)}
                      onChange={(event) => {
                        setDerivedFrom((current) => event.target.checked
                          ? [...current, module.code].slice(0, PROTOTYPE_LIMITS.derivedFromMax)
                          : current.filter((code) => code !== module.code));
                      }}
                    >
                      <span dir="ltr" className="me-2">{module.code}</span>
                      <span className="text-xs text-portal-ink-muted">
                        {module.name}{DOT}{module.version}
                        {module.vendor ? ` · ${module.vendor}` : ''}
                      </span>
                    </StudioCheckbox>
                  ))}
                </div>
              </fieldset>
            )}
            {derivedFrom.length > 0 && (
              <p className="text-xs text-portal-ink-muted">{t('moduleStudio.prototype.dialog.derivationNote')}</p>
            )}

            <fieldset className="rounded-md border border-portal-border p-2">
              <legend className="text-xs text-portal-ink-muted">
                {t('moduleStudio.prototype.dialog.derivationIntent')}
              </legend>
              {DERIVATION_INTENTS.map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm text-portal-ink">
                  <input
                    type="radio"
                    name="derivation"
                    checked={derivation === value}
                    disabled={value === 'standard'}
                    onChange={() => setDerivation(value)}
                  />
                  {value === 'none' && t('moduleStudio.prototype.derivation.none')}
                  {value === 'on-request' && t('moduleStudio.prototype.derivation.onRequest')}
                  {value === 'standard' && (
                    <>
                      {t('moduleStudio.prototype.derivation.standard')}
                      <span className="text-xs text-portal-ink-muted">
                        ({t('moduleStudio.prototype.derivation.comingSoon')})
                      </span>
                    </>
                  )}
                </label>
              ))}
              <p className="mt-1 text-xs text-portal-ink-muted">
                {t('moduleStudio.prototype.dialog.intentNote')}
              </p>
            </fieldset>

            <p className="rounded-md border border-portal-border bg-portal-surface-muted px-3 py-2 text-sm text-portal-ink">
              {t('moduleStudio.prototype.rights.text')}
            </p>
            <StudioCheckbox checked={attestationNoRealData} onChange={(event) => setAttestationNoRealData(event.target.checked)}>
              {t('moduleStudio.prototype.attestation.noRealData')}
            </StudioCheckbox>
            <StudioCheckbox checked={attestationRights} onChange={(event) => setAttestationRights(event.target.checked)}>
              {t('moduleStudio.prototype.attestation.rights')}
            </StudioCheckbox>
          </div>
        )}

        {step === 4 && preview && (
          <div className="flex flex-col gap-4">
            {htmls.length > 0 && preview.docs[sourcePosition] && (
              <div className="flex flex-col gap-2">
                <SourceSelector
                  sources={htmls.map((source, rank) => ({ sourceIndex: rank, fileName: source.fileName }))}
                  selected={sourcePosition}
                  onSelect={setPreviewSource}
                />
                <PrototypeSandbox
                  key={sourcePosition}
                  html={htmls[sourcePosition].text}
                  title={
                    htmls.length > 1
                      ? `${title || htmls[sourcePosition].fileName}${DASH}${t('moduleStudio.prototype.source.short', { n: sourcePosition + 1 })}`
                      : title || htmls[sourcePosition].fileName
                  }
                  importId={null}
                  capture={false}
                  autoSnapshot={false}
                  externalHosts={externalResourceHosts(preview.docs[sourcePosition])}
                  captureLabel=""
                />
              </div>
            )}
            {markdowns.length > 0 && (
              <section aria-label={t('moduleStudio.prototype.dialog.documents')}>
                <h4 className="text-sm font-medium text-portal-ink">{t('moduleStudio.prototype.dialog.documents')}</h4>
                <ul className="m-0 list-none p-0 text-sm text-portal-ink">
                  {markdowns.map((markdown) => (
                    <li key={markdown.fileName} className="flex flex-wrap gap-x-2">
                      <span dir="ltr" className="break-all">{markdown.fileName}</span>
                      <span className="text-portal-ink-muted">
                        {DOT}{prototypeDocumentRoleLabel(t, markdown.role)}{DOT}{formatPieceSize(intl, markdown.bytes.length)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {images.map((image) => (
                  <img
                    key={image.fileName}
                    src={thumbnailsRef.current.get(image.fileName)}
                    alt={image.fileName}
                    className="h-24 rounded border border-portal-border object-cover"
                  />
                ))}
              </div>
            )}
            {preview.inventory && (
              <details>
                <summary className="cursor-pointer text-sm font-medium text-portal-ink">
                  {t('moduleStudio.prototype.dialog.indicativeInventory')}
                </summary>
                <p className="mt-1 text-xs text-portal-ink-muted">
                  {t('moduleStudio.prototype.dialog.scriptBuiltNote')}
                </p>
                <PrototypeInventoryView
                  inventory={preview.inventory}
                  sourceFileNames={htmls.map((source) => source.fileName)}
                />
              </details>
            )}
            <div>
              <h4 className="text-sm font-medium text-portal-ink">
                {t('moduleStudio.prototype.dialog.previewWarnings')}
              </h4>
              <PrototypeWarningsView warnings={preview.warnings} preview />
            </div>
            {(!attestationNoRealData || !attestationRights) && (
              // Les attestations se cochent à l'étape 3 (sous le catalogue des modules) ; quand il en manque une,
              // l'étape 4 la remontre ici plutôt que de renvoyer à l'étape précédente (retour d'Olivier, 12.10).
              <div className="flex flex-col gap-2 rounded-md border border-warning-solid bg-warning-subtle p-3">
                <StudioCheckbox checked={attestationNoRealData} onChange={(event) => setAttestationNoRealData(event.target.checked)}>
                  {t('moduleStudio.prototype.attestation.noRealData')}
                </StudioCheckbox>
                <StudioCheckbox checked={attestationRights} onChange={(event) => setAttestationRights(event.target.checked)}>
                  {t('moduleStudio.prototype.attestation.rights')}
                </StudioCheckbox>
              </div>
            )}
            {failedSources.length > 0 && (
              <div className="rounded-md border border-warning-solid bg-warning-subtle p-3">
                <p className="text-sm text-warning-text">{t('moduleStudio.prototype.dialog.failedSources')}</p>
                <ul className="text-sm text-portal-ink">
                  {failedSources.map((source) => <li key={source.fileName}>{source.fileName}</li>)}
                </ul>
              </div>
            )}
            {failedImages.length > 0 && (
              <div className="rounded-md border border-warning-solid bg-warning-subtle p-3">
                <p className="text-sm text-warning-text">{t('moduleStudio.prototype.dialog.failedImages')}</p>
                <ul className="text-sm text-portal-ink">
                  {failedImages.map((image) => <li key={image.fileName}>{image.fileName}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {step > 1 && (
            <button
              type="button"
              className="rounded-md border border-portal-border bg-portal-surface px-3 py-1.5 text-sm text-portal-ink"
              onClick={() => setStep((current) => importMetadataReviewed(parameters) ? 1 : Math.max(1, current - 1))}
              disabled={sending}
            >
              {t('moduleStudio.prototype.dialog.previous')}
            </button>
          )}
          {step < 4 && (
            <button
              type="button"
              className="rounded-md border border-portal-border bg-portal-accent px-3 py-1.5 text-sm text-portal-ink-on-accent"
              onClick={() => setStep((current) => importMetadataReviewed(parameters) ? 4 : Math.min(4, current + 1))}
            >
              {t('moduleStudio.prototype.dialog.next')}
            </button>
          )}
          {step === 4 && (failedImages.length > 0 || failedSources.length > 0) && (
            <button
              type="button"
              className="rounded-md border border-portal-border bg-portal-surface px-3 py-1.5 text-sm text-portal-ink"
              onClick={() => void retryFailedParts()}
              disabled={sending}
            >
              {t('moduleStudio.prototype.dialog.retry')}
            </button>
          )}
          {step === 4 && (
            <button
              type="button"
              className="rounded-md border border-portal-border bg-portal-accent px-3 py-1.5 text-sm text-portal-ink-on-accent disabled:opacity-50"
              disabled={blockers.length > 0 || sending}
              onClick={() => void send()}
            >
              {t('moduleStudio.prototype.dialog.import')}
            </button>
          )}
          {step === 4 && blockers.length > 0 && (
            <span className="text-xs text-portal-ink-muted">
              {importBlockerLabel(t, blockers[0])}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}
